/**
 * Normalizacoes usadas pelos importadores (lado cliente = preview, lado server = insert).
 * Portadas do apolonia_v2 (PhpSpreadsheet) pra TypeScript. Mantem comportamento equivalente.
 */

const PT_PREPOSITIONS = new Set([
  "de", "da", "do", "das", "dos", "e", "em", "a", "o", "as", "os",
  "na", "no", "nas", "nos", "por", "com", "para", "di", "del",
]);

export type NameFormat = "original" | "titlecase" | "uppercase";

export function formatPatientName(raw: string, mode: NameFormat): string {
  const value = raw.trim();
  if (!value) return value;
  if (mode === "uppercase") return value.toUpperCase();
  if (mode === "original") return value;
  const words = value.toLowerCase().split(/\s+/);
  return words
    .map((word, i) => {
      if (i !== 0 && PT_PREPOSITIONS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/** Retorna soh digitos. Valida 10 ou 11 (formato BR). Retorna undefined se nao encaixar. */
export function normalizePhoneBR(raw: string): string | undefined {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 || digits.length === 10) return digits;
  if (digits.length === 13 && digits.startsWith("55")) {
    const inner = digits.slice(2);
    if (inner.length === 11 || inner.length === 10) return inner;
  }
  if (digits.length === 9 || digits.length === 8) return digits;
  return undefined;
}

/** Retorna 11 digitos se for CPF valido em comprimento. Senao retorna undefined. */
export function normalizeCPF(raw: string): string | undefined {
  const digits = raw.replace(/\D/g, "");
  return digits.length === 11 ? digits : undefined;
}

export function normalizeSex(raw: string): "M" | "F" | undefined {
  const first = raw.trim().charAt(0).toUpperCase();
  return first === "M" || first === "F" ? first : undefined;
}

/** Converte "1", "true", "sim", "ativo" em true. */
export function normalizeBoolean(raw: string): boolean {
  const low = raw.trim().toLowerCase();
  return ["1", "true", "sim", "yes", "ativo", "active", "s", "y", "x"].includes(low);
}

/**
 * Parse de data robusto — aceita:
 *  - Excel serial (numero)
 *  - DD/MM/YYYY, DD-MM-YYYY
 *  - YYYY-MM-DD, YYYY/MM/DD
 *  - ISO 8601 (qualquer coisa parseavel por Date)
 *
 * Retorna Date (00:00 UTC no dia) ou undefined.
 */
export function parseDateFlexible(raw: string): Date | undefined {
  const value = raw.trim();
  if (!value) return undefined;

  // UTC NOON em vez de midnight — evita shift de dia em timezones negativos (BR = UTC-3)
  if (/^\d+(\.\d+)?$/.test(value)) {
    const n = parseFloat(value);
    if (n > 100 && n < 100000) {
      const excelEpoch = Date.UTC(1899, 11, 30);
      const ms = excelEpoch + Math.floor(n) * 86400 * 1000 + 12 * 3600 * 1000;
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d;
    }
  }

  let m = value.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const d = new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]), 12));
    if (!isNaN(d.getTime())) return d;
  }

  m = value.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
  if (m) {
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
    if (!isNaN(d.getTime())) return d;
  }

  const fallback = new Date(value);
  if (!isNaN(fallback.getTime())) {
    return new Date(Date.UTC(
      fallback.getUTCFullYear(),
      fallback.getUTCMonth(),
      fallback.getUTCDate(),
      12,
    ));
  }

  return undefined;
}

/** Normaliza "HH:MM" ou "H:MM" para "HH:MM" com pad. Retorna undefined se invalido. */
export function normalizeTime(raw: string): string | undefined {
  const m = raw.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return undefined;
  const hh = m[1].padStart(2, "0");
  const mm = m[2];
  const h = Number(hh);
  const mn = Number(mm);
  if (h > 23 || mn > 59) return undefined;
  return `${hh}:${mm}`;
}

/** lower + sem acento + sem separadores — pra matching de aliases de colunas. */
export function normalizeColumnKey(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s_\-\.\/]+/g, "");
}
